import React, { useRef, useEffect, useCallback, forwardRef, useImperativeHandle } from "react";

/* ═══════════════════════════════════════════════════════════════
   HELPERS
═══════════════════════════════════════════════════════════════ */

function getTransform(s0, s1, s2, d0, d1, d2) {
  const denom =
    s0.x * (s1.y - s2.y) +
    s1.x * (s2.y - s0.y) +
    s2.x * (s0.y - s1.y);

  if (Math.abs(denom) < 1e-10) return null;

  const a =
    (d0.x * (s1.y - s2.y) + d1.x * (s2.y - s0.y) + d2.x * (s0.y - s1.y)) / denom;
  const b =
    (d0.x * (s2.x - s1.x) + d1.x * (s0.x - s2.x) + d2.x * (s1.x - s0.x)) / denom;
  const c =
    (d0.x * (s1.x * s2.y - s2.x * s1.y) +
      d1.x * (s2.x * s0.y - s0.x * s2.y) +
      d2.x * (s0.x * s1.y - s1.x * s0.y)) /
    denom;
  const d =
    (d0.y * (s1.y - s2.y) + d1.y * (s2.y - s0.y) + d2.y * (s0.y - s1.y)) / denom;
  const e =
    (d0.y * (s2.x - s1.x) + d1.y * (s0.x - s2.x) + d2.y * (s1.x - s0.x)) / denom;
  const f =
    (d0.y * (s1.x * s2.y - s2.x * s1.y) +
      d1.y * (s2.x * s0.y - s0.x * s2.y) +
      d2.y * (s0.x * s1.y - s1.x * s0.y)) /
    denom;

  return [a, d, b, e, c, f];
}

/* ── expand a triangle's points slightly outward from its centroid ──
   Adjacent clipped triangles otherwise leave a thin anti-aliased seam
   at every shared edge; across an 8×8 grid that reads as a visible
   grid of horizontal/vertical lines over the photo. A sub-pixel
   overlap hides the seam without visibly distorting the image. */
function expandTriangle(p0, p1, p2, amount) {
  const cx = (p0.x + p1.x + p2.x) / 3;
  const cy = (p0.y + p1.y + p2.y) / 3;
  const push = (p) => {
    const dx = p.x - cx;
    const dy = p.y - cy;
    const len = Math.sqrt(dx * dx + dy * dy) || 1;
    return { x: p.x + (dx / len) * amount, y: p.y + (dy / len) * amount };
  };
  return [push(p0), push(p1), push(p2)];
}

function drawImageInQuad(ctx, img, TL, TR, BR, BL, subdivisions = 8) {
  const w = img.naturalWidth;
  const h = img.naturalHeight;
  if (!w || !h) return;

  // Overlap in canvas pixels — small enough to be invisible, large enough
  // to fully cover the anti-aliased seam between adjacent triangles.
  const OVERLAP = 1.25;

  for (let row = 0; row < subdivisions; row++) {
    for (let col = 0; col < subdivisions; col++) {
      const r0 = row / subdivisions;
      const r1 = (row + 1) / subdivisions;
      const c0 = col / subdivisions;
      const c1 = (col + 1) / subdivisions;

      const p = (r, c) => ({
        x:
          (1 - r) * (1 - c) * TL.x +
          (1 - r) * c * TR.x +
          r * c * BR.x +
          r * (1 - c) * BL.x,
        y:
          (1 - r) * (1 - c) * TL.y +
          (1 - r) * c * TR.y +
          r * c * BR.y +
          r * (1 - c) * BL.y,
      });

      const p00 = p(r0, c0);
      const p01 = p(r0, c1);
      const p11 = p(r1, c1);
      const p10 = p(r1, c0);

      const sx0 = c0 * w, sy0 = r0 * h;
      const sx1 = c1 * w, sy1 = r1 * h;

      // Triangle 1 — clip path expanded to overlap neighbors, but the
      // transform mapping still uses the ORIGINAL (unexpanded) corners
      // so the image content itself isn't stretched or distorted.
      const [e00a, e01a, e11a] = expandTriangle(p00, p01, p11, OVERLAP);
      ctx.save();
      ctx.beginPath();
      ctx.moveTo(e00a.x, e00a.y);
      ctx.lineTo(e01a.x, e01a.y);
      ctx.lineTo(e11a.x, e11a.y);
      ctx.closePath();
      ctx.clip();
      const m1 = getTransform(
        { x: sx0, y: sy0 }, { x: sx1, y: sy0 }, { x: sx1, y: sy1 },
        p00, p01, p11
      );
      if (m1) { ctx.transform(m1[0], m1[1], m1[2], m1[3], m1[4], m1[5]); ctx.drawImage(img, 0, 0); }
      ctx.restore();

      // Triangle 2 — same treatment
      const [e00b, e11b, e10b] = expandTriangle(p00, p11, p10, OVERLAP);
      ctx.save();
      ctx.beginPath();
      ctx.moveTo(e00b.x, e00b.y);
      ctx.lineTo(e11b.x, e11b.y);
      ctx.lineTo(e10b.x, e10b.y);
      ctx.closePath();
      ctx.clip();
      const m2 = getTransform(
        { x: sx0, y: sy0 }, { x: sx1, y: sy1 }, { x: sx0, y: sy1 },
        p00, p11, p10
      );
      if (m2) { ctx.transform(m2[0], m2[1], m2[2], m2[3], m2[4], m2[5]); ctx.drawImage(img, 0, 0); }
      ctx.restore();
    }
  }
}

/* ─── helper: load an image safely, resolving even on error ─── */
function loadImage(src) {
  return new Promise((resolve) => {
    if (!src) return resolve(null);

    const img = new Image();

    if (!src.startsWith("data:")) {
      img.crossOrigin = "anonymous";
    }

    img.onload = () => resolve(img);
    img.onerror = () => {
      if (!src.startsWith("data:") && img.crossOrigin) {
        const fallback = new Image();
        fallback.onload = () => resolve(fallback);
        fallback.onerror = () => resolve(null);
        fallback.src = src + (src.includes("?") ? "&" : "?") + "_nocors=" + Date.now();
        return;
      }
      resolve(null);
    };

    img.src = src;
  });
}

/* ═══════════════════════════════════════════════════════════════
   COMPONENT
═══════════════════════════════════════════════════════════════ */

const FrameCompositor = forwardRef(
  (
    {
      previewImageUrl,
      userPhotos = [],
      photoZones = [],
      style = {},
      className = "",
      outputWidth = 1200,
    },
    ref
  ) => {
    const canvasRef   = useRef(null);
    const bgImgRef    = useRef(null);
    const userImgRefs = useRef([]);
    const roRef       = useRef(null);

    const sizeCanvas = useCallback(() => {
      const canvas = canvasRef.current;
      const bg = bgImgRef.current;
      if (!canvas || !bg || !bg.naturalWidth) return false;

      const parent = canvas.parentElement;
      const displayWidth = parent?.clientWidth || 400;
      const dpr = Math.min(window.devicePixelRatio || 1, 3);

      const targetWidth = Math.max(outputWidth, Math.round(displayWidth * dpr));
      const newWidth  = targetWidth;
      const newHeight = Math.round(targetWidth * (bg.naturalHeight / bg.naturalWidth));

      if (canvas.width !== newWidth || canvas.height !== newHeight) {
        canvas.width  = newWidth;
        canvas.height = newHeight;
        return true;
      }
      return false;
    }, [outputWidth]);

    /* ── DRAW ── */
    const draw = useCallback(() => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const bg = bgImgRef.current;
      if (!bg || !bg.complete || !bg.naturalWidth) return;

      const ctx = canvas.getContext("2d");
      const cw  = canvas.width;
      const ch  = canvas.height;

      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = "high";

      ctx.clearRect(0, 0, cw, ch);

      // 1. Background frame
      ctx.drawImage(bg, 0, 0, cw, ch);

      // 2. User photos into zones
      photoZones.forEach((zone, i) => {
        const userImg = userImgRefs.current[i];

        if (zone.quad) {
          const { TL, TR, BR, BL } = zone.quad;
          const toPx = (pt) => ({ x: (pt.x / 100) * cw, y: (pt.y / 100) * ch });
          const qTL = toPx(TL), qTR = toPx(TR), qBR = toPx(BR), qBL = toPx(BL);

          if (userImg && userImg.complete && userImg.naturalWidth) {
            drawImageInQuad(ctx, userImg, qTL, qTR, qBR, qBL);
          } else {
            // Empty zone placeholder — soft fill only, no dashed line
            ctx.save();
            ctx.beginPath();
            ctx.moveTo(qTL.x, qTL.y);
            ctx.lineTo(qTR.x, qTR.y);
            ctx.lineTo(qBR.x, qBR.y);
            ctx.lineTo(qBL.x, qBL.y);
            ctx.closePath();
            ctx.fillStyle = "rgba(200,200,200,0.22)";
            ctx.fill();
            ctx.restore();
          }
        } else {
          const x = (zone.x      / 100) * cw;
          const y = (zone.y      / 100) * ch;
          const w = (zone.width  / 100) * cw;
          const h = (zone.height / 100) * ch;

          if (userImg && userImg.complete && userImg.naturalWidth) {
            const imgW  = userImg.naturalWidth;
            const imgH  = userImg.naturalHeight;
            const scale = Math.max(w / imgW, h / imgH);
            const drawW = imgW * scale;
            const drawH = imgH * scale;
            const dx    = x + (w - drawW) / 2;
            const dy    = y + (h - drawH) / 2;

            ctx.save();
            ctx.beginPath();
            ctx.rect(x, y, w, h);
            ctx.clip();
            ctx.drawImage(userImg, dx, dy, drawW, drawH);
            ctx.restore();
          } else {
            ctx.fillStyle = "rgba(200,200,200,0.25)";
            ctx.fillRect(x, y, w, h);
          }
        }
      });
    }, [photoZones]);

    const resizeAndDraw = useCallback(() => {
      sizeCanvas();
      draw();
    }, [sizeCanvas, draw]);

    useEffect(() => {
      if (!previewImageUrl) return;

      let cancelled = false;

      loadImage(previewImageUrl).then((img) => {
        if (cancelled) return;
        bgImgRef.current = img;
        resizeAndDraw();
      });

      return () => { cancelled = true; };
    }, [previewImageUrl, resizeAndDraw]);

    useEffect(() => {
      let cancelled = false;

      userImgRefs.current = new Array(photoZones.length).fill(null);
      draw();

      photoZones.forEach((_, i) => {
        const src = userPhotos[i];
        if (!src) return;

        loadImage(src).then((img) => {
          if (cancelled) return;
          userImgRefs.current[i] = img;
          draw();
        });
      });

      return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userPhotos, photoZones]);

    useEffect(() => {
      const canvas = canvasRef.current;
      const parent = canvas?.parentElement;
      if (!parent) return;

      const ro = new ResizeObserver(() => {
        resizeAndDraw();
      });
      ro.observe(parent);
      roRef.current = ro;

      window.addEventListener("resize", resizeAndDraw);

      return () => {
        ro.disconnect();
        window.removeEventListener("resize", resizeAndDraw);
      };
    }, [resizeAndDraw]);

    useImperativeHandle(ref, () => ({
      getBlob: () =>
        new Promise((resolve, reject) => {
          const canvas = canvasRef.current;
          if (!canvas) return reject("Canvas not ready");
          canvas.toBlob(
            (blob) => {
              if (!blob) return reject("Blob generation failed");
              resolve(blob);
            },
            "image/jpeg",
            0.95
          );
        }),
    }));

    return (
      <div
        className={className}
        style={{ width: "100%", height: "100%", lineHeight: 0, background: "#fff", ...style }}
      >
        <canvas
          ref={canvasRef}
          style={{
            width: "100%",
            height: "100%",
            display: "block",
            borderRadius: "6px",
            objectFit: "cover",
            background: "#fff",
          }}
        />
      </div>
    );
  }
);

FrameCompositor.displayName = "FrameCompositor";
export default FrameCompositor;